import { useEffect, useRef, useState } from 'react';
import { useAuth } from '@entities/auth';
import { useRefreshReferralCode } from './use-refresh-referral-code';

type EnsurePhase = 'idle' | 'ensuring' | 'done' | 'failed';

export const useEnsureReferralCode = () => {
  const { user, isLoading: isAuthLoading } = useAuth();
  const { refreshReferralCode, error: refreshError } = useRefreshReferralCode();
  const refreshReferralCodeRef = useRef(refreshReferralCode);
  refreshReferralCodeRef.current = refreshReferralCode;

  const [phase, setPhase] = useState<EnsurePhase>('idle');
  const ensureStartedRef = useRef(false);

  const hasReferralCode = Boolean(user?.referralCode);
  const shouldEnsure = !isAuthLoading && Boolean(user) && !hasReferralCode;

  useEffect(() => {
    if (isAuthLoading) {
      return;
    }

    if (hasReferralCode) {
      setPhase('done');
      return;
    }

    if (!user || ensureStartedRef.current) {
      return;
    }

    ensureStartedRef.current = true;
    setPhase('ensuring');

    void (async () => {
      const result = await refreshReferralCodeRef.current();
      setPhase(result.success ? 'done' : 'failed');
    })();
  }, [hasReferralCode, isAuthLoading, user]);

  const isReferralBootstrapPending = shouldEnsure && (phase === 'idle' || phase === 'ensuring');

  const isReferralDataEnabled =
    !isAuthLoading && Boolean(user) && (hasReferralCode || phase === 'done' || phase === 'failed');

  return {
    isReferralBootstrapPending,
    isReferralDataEnabled,
    referralBootstrapError: phase === 'failed' ? refreshError : null,
  };
};
